import { type TextareaAutosizeProps } from '@mui/material/TextareaAutosize';
import { useTextareaStyles } from './textarea.styles';
import { TextareaResize } from './textareaResize';
import { useTheme } from '../../../../theme';
import { type CSSObject, css as muiCss } from '@emotion/react';
import { useState, type JSX, type ChangeEvent } from 'react';
import { Status } from '../../status';

export type TextareaProps = {
  onChange?: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    value: string,
  ) => void;
  maxLength?: HTMLInputElement['maxLength'];
  value?: string;
  hasError?: boolean;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  hideErrorAlert?: boolean;
  noResize?: boolean;
  contentEditable?: boolean;
  css?: CSSObject;
} & Omit<TextareaAutosizeProps, 'onChange' | 'error'>;

export const Textarea = ({
  onChange,
  onFocus,
  onBlur,
  hasError = false,
  hideErrorAlert = false,
  noResize = false,
  disabled = false,
  contentEditable = true,
  css,
  ...props
}: TextareaProps): JSX.Element => {
  const [isFocused, setIsFocused] = useState(false);
  const theme = useTheme();
  const styles = useTextareaStyles({
    noResize,
    hasError,
    disabled,
    contentEditable,
  });

  const wrapperClasses = [
    'textarea-wrapper',
    (Boolean(disabled) || !contentEditable) && 'textarea-disabled',
    Boolean(isFocused) && 'textarea-focused',
  ];

  return (
    <div
      css={styles.wrapper}
      className={wrapperClasses.filter(Boolean).join(' ')}
    >
      {!hideErrorAlert && hasError && (
        <Status statusLevel='error' size={20} css={styles.error} />
      )}
      <textarea
        css={muiCss(styles.textarea, css)}
        onChange={(e) => onChange?.(e, e.target.value)}
        disabled={disabled || !contentEditable}
        {...props}
        onFocus={(e) => {
          setIsFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          onBlur?.(e);
        }}
      />
      <TextareaResize
        aria-hidden
        size={26}
        css={{
          ...styles.resizer,
          color:
            disabled || !contentEditable
              ? theme.icon.tertiary
              : theme.icon.secondary,
        }}
      />
    </div>
  );
};
